import PropTypes from 'prop-types'

import Radio from '../../../common/components/Radio.js'
import {
  REPORT_EXPORT_PRODUCTS_INVENTORY,
  REPORT_EXPORT_PRODUCTS_NO_INVENTORY,
  REPORT_EXPORT_PRODUCTS_SUPPLIERS,
} from '../../../common/constants/Reports.js'
import {reportSelector} from '../../../data/reports.js'
import {
  formSelector,
  getState,
  updateForm,
  useForm,
  useSelector,
} from '../../../store.js'
import {ensureArrayOfNumbers} from '../../../common/ensure.js'
import {cleanProductQuery} from '../../ProductListPage/productListFunctions.js'
import ProductFilters from './ProductFilters.js'
import {reportOptionsByValueSelector} from '../reportsPageSelectors.js'

/**
 * Take saved report params and transform them into a useable shape for querying.
 * It also translates old params from before we switched this report to use the
 * GET /product query param shape.
 *
 * @param {object} params object of params, either the old shape or one that
 *  mirrors GET /product query params
 * @returns {object} query object used by GET /product
 */
function translateParamsProductListQuery(params) {
  const productQuery = {
    ...params,
  }

  if (params.warehouse_ids) {
    productQuery.warehouse_id = ensureArrayOfNumbers(params.warehouse_ids)
  }

  return cleanProductQuery(productQuery)
}

export function setupExportProductsForm(params = {}) {
  let {
    reportID,
    productQuery = {},
    queryTotalCount = null,
    selectedSKUs = [],
  } = params

  let {params: reportParams} = reportID
    ? reportSelector(getState(), {reportID})
    : {params: productQuery}

  productQuery = translateParamsProductListQuery(reportParams)

  delete productQuery.sort
  delete productQuery.limit
  delete productQuery.offset

  const querySelection = selectedSKUs.length ? 'selection' : 'query'

  return {
    querySelection,
    productQuery,
    queryTotalCount,
    selectedSKUs,
  }
}

export function exportProductsErrorsSelector() {
  return {}
}

export function exportProductsPayloadSelector(state, {formName}) {
  const {reportType, productQuery, selectedSKUs, querySelection} = formSelector(
    state,
    {formName},
  )

  let params = productQuery

  if (querySelection === 'selection') {
    params = {sku: selectedSKUs}

    if (productQuery.warehouse_id && productQuery.warehouse_id.length) {
      params.warehouse_id = productQuery.warehouse_id
    }
  }

  return {
    type: reportType,
    params,
  }
}

export default function ExportProductsForm({formName}) {
  const form = useForm(formName)
  const reportOptionsByValue = useSelector(reportOptionsByValueSelector)
  const productExportOption =
    reportOptionsByValue[REPORT_EXPORT_PRODUCTS_INVENTORY] ||
    reportOptionsByValue[REPORT_EXPORT_PRODUCTS_NO_INVENTORY]
  const supplierExportOption =
    reportOptionsByValue[REPORT_EXPORT_PRODUCTS_SUPPLIERS]

  return (
    <ul className="list list--no-style">
      {productExportOption && supplierExportOption && (
        <li className="list__item--form padding-bottom-20">
          <p className="fs-00 margin-top-20 margin-bottom-10">
            <strong>Type of product export:</strong>
          </p>
          {form.reportID ? (
            <div className="margin-bottom-10">
              <strong>{reportOptionsByValue[form.reportType].display}</strong>
            </div>
          ) : (
            <>
              <div className="margin-bottom-10">
                <Radio
                  mode="fancy"
                  className="margin-left-n03"
                  id="warehouse_export"
                  label={productExportOption.display}
                  checked={form.reportType === productExportOption.value}
                  onChange={() =>
                    updateForm(formName, {
                      reportType: productExportOption.value,
                    })
                  }
                />
              </div>
              <Radio
                mode="fancy"
                className="margin-left-n03"
                id="supplier_export"
                label={supplierExportOption.display}
                checked={form.reportType === supplierExportOption.value}
                onChange={() =>
                  updateForm(formName, {
                    reportType: supplierExportOption.value,
                  })
                }
              />
            </>
          )}
        </li>
      )}
      <ProductFilters formName={formName} />
    </ul>
  )
}

ExportProductsForm.propTypes = {
  formName: PropTypes.string.isRequired,
}
